import React, { useState, useEffect } from 'react';
import { signIn } from 'aws-amplify/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingScreen from './LoadingScreen';
import {
  Box,
  Button,
  Container,
  Form,
  FormField,
  Header,
  Input,
  SpaceBetween,
  Alert,
  Grid
} from '@cloudscape-design/components';

function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuthState } = useAuth();

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      navigate('/login', { replace: true });
    }
  }, [location.state, navigate]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }

    setError('');
    setSuccessMessage('');
    setUnverifiedEmail('');
    setIsLoading(true);

    try {
      const result = await signIn({ username, password });

      // Check if user needs to confirm their account
      if (result?.nextStep?.signInStep === 'CONFIRM_SIGN_UP' || 
          result?.nextStep?.signInStep === 'CONFIRM_SIGN_IN' ||
          !result?.isSignedIn) {
        // Automatically forward to verification page
        navigate('/register', {
          state: {
            email: username,
            needsVerification: true
          }
        });
        return;
      }

      try {
        await checkAuthState();
        navigate('/recordings');
      } catch (authError) {
        setError('Authentication successful but failed to load user data. Please try refreshing the page.');
        setIsLoading(false);
      }
    } catch (err) {
      const errorName = err?.name || '';

      if (errorName === 'UserNotConfirmedException' || err?.message?.includes('not confirmed')) {
        // Automatically forward to verification page
        navigate('/register', {
          state: {
            email: username,
            needsVerification: true
          }
        });
        return;
      } else if (errorName === 'NotAuthorizedException') {
        setError('Incorrect username or password.');
      } else if (errorName === 'UserNotFoundException') {
        setError('No account found with this username.');
      } else {
        setError(err?.message || 'Sign in failed. Please try again.');
      }
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return <LoadingScreen message="Signing you in" />;
  }

  return (
    <div style={{ display: "flex", justifyContent: "center", paddingTop: "20px" }}>
      <Container>
        <SpaceBetween size="l">
          <Header
            variant="h1"
            description="Sign in to access Recapify features"
            textAlign="center"
          >
            Welcome to Recapify
          </Header>

          {successMessage && (
            <Alert type="success" header="Success">
              {successMessage}
            </Alert>
          )}

          {error && (
            <Alert type="error" header="Error">
              {error}
            </Alert>
          )}

          <Form
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="link"
                  onClick={() => navigate('/register', {
                    state: unverifiedEmail ? {
                      email: unverifiedEmail,
                      needsVerification: true
                    } : undefined
                  })}
                >
                  Register
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                  Sign In
                </Button>
              </SpaceBetween>
            }
          >
            <SpaceBetween size="l">
              <FormField
                label="Username"
                description="Enter your username or email address"
              >
                <Input
                  type="text"
                  value={username}
                  onChange={({ detail }) => setUsername(detail.value)}
                />
              </FormField>

              <FormField
                label="Password"
                description="Enter your password"
              >
                <Input
                  type="password"
                  value={password}
                  onChange={({ detail }) => setPassword(detail.value)}
                />
              </FormField>
            </SpaceBetween>
          </Form>
        </SpaceBetween>
      </Container>
    </div>
  );
}

export default Login;
